<template>
	<BaseCmsPage v-slot="{page}" @load="onPageLoad">
		<div class="auth-section wrapper">
			<AuthSidebar v-if="!mobileBreakpoint" />
			<div class="auth-content">
				<h1 class="auth-main-title" v-html="page?.seo_h1"></h1>
				<AuthSidebar v-if="mobileBreakpoint" />
				<ClientOnly>
					<BaseAuthEditForm v-slot="{loading, apiErrors, errors, status}" @load="onFormLoad" :fields-config="fieldsConfig">
						<div class="auth-edit-profile-form form-animated-label" :class="{'loading': loading}">
							<BaseCmsLabel v-if="status" tag="div" class="global-success" :code="status.data.label_name" />
							<template v-else-if="apiErrors?.length">
								<div class="global-error" v-for="apiError in apiErrors" :key="apiError">{{ apiError.field }}: {{ apiError.error }}</div>
							</template>
							<template v-if="Object.keys(errors)?.length">
								<BaseCmsLabel tag="div" class="global-error" code="form_validation_error" data-scroll-to-error />
							</template>

							<template v-if="!formFields?.length">
								<BaseThemeUiLoading />
							</template>
							<template v-else>
								<!-- Regular fields -->
								<template v-for="item in formFields" :key="item.name">
									<BaseFormField :item="item" v-slot="{errorMessage, floatingLabel, isTouched, value}">
										<p v-if="item.name != 'newsletter'" class="field" :class="['field-' + item.name, {'ffl-floated': floatingLabel}, {'err': errorMessage}, {'success': !errorMessage && isTouched && value}]">
											<BaseCmsLabel :class="{'label-gender': item.name == 'gender'}" tag="label" :for="item.name" :code="item.name" />
											<BaseFormInput :id="item.name" />
											<span class="error" v-show="errorMessage" v-html="errorMessage" />
										</p>
									</BaseFormField>
								</template>

								<!-- GDPR -->
								<div v-if="gdprAllField">
									<div class="signup-note" style="display: block;" v-if="gdprAllField?.extra_info?.text_header" v-html="gdprAllField.extra_info.text_header" />
									<div class="signup-gdpr-checkbox">
										<BaseFormField :item="gdprAllField" v-slot="{errorMessage}">
											<p class="field field-gdpr_template_api">
												<BaseFormInput :id="gdprAllField.name" :value="gdprAllField?.value" @click="onToggleAllGdpr(gdprAllField.name)" />
												<BaseCmsLabel :for="gdprAllField.name" tag="label" code="gdpr_select_all" />
												<span class="signup-desc active">
													<span class="signup-desc-cnt signup-desc-cnt-special" @click="activeGdpr = !activeGdpr">
														<BaseCmsLabel tag="span" code="gdpr_select_all_tips" />
													</span>
												</span>
												<span class="error" v-show="errorMessage" v-html="errorMessage" />
											</p>
										</BaseFormField>

										<div class="signup-gdpr-checkbox-wrapper" :class="{'active': activeGdpr}">
											<template v-for="item in gdprFields" :key="item.name">
												<BaseFormField :item="item" v-slot="{errorMessage}">
													<p class="field field-gdpr_template_api">
														<BaseFormInput :value="item.value" :id="item.name" @click="onToggleGdpr(item.name)" />
														<label :for="item.name">{{item.title}}</label>
														<BaseUiAccordion>
															<BaseUiAccordionPanel :id="item.name" v-slot="{onToggle, active}">
																<span class="signup-desc" :class="{'active': active}">
																	<span class="signup-desc-link" @click="onToggle">
																		<span v-if="!active" class="btn-inactive">Prikaži več</span>
																		<span v-if="active" class="btn-active">Skrij</span>
																	</span>
																	<span class="signup-desc-cnt" v-html="item.description" />
																</span>
															</BaseUiAccordionPanel>
														</BaseUiAccordion>
														<span class="error" v-show="errorMessage" v-html="errorMessage" />
													</p>
												</BaseFormField>
											</template>
										</div>
									</div>
									<div class="signup-note" v-if="gdprAllField?.extra_info?.text_footer" v-html="gdprAllField.extra_info.text_footer" v-interpolation />
								</div>

								<button v-if="loading" class="btn-lightBlue btn-medium btn-loading"></button>
								<button v-else class="btn-lightBlue btn-medium" type="submit">
									<BaseCmsLabel code="save" />
								</button>
							</template>
						</div>
					</BaseAuthEditForm>
					<template #fallback>
						<BaseThemeUiLoading />
					</template>
				</ClientOnly>
			</div>
		</div>
	</BaseCmsPage>
</template>

<script setup>
	const labels = useLabels();
	const fieldsConfig = {
		gender: {
			type: 'radio'
		}
	}

	//rwd
	const {mobileBreakpoint} = inject('rwd');

	// restructure form fields
	const formFields = ref([]);
	const genderField = ref(null);
	const gdprFields = ref([]);
	const gdprAllField = ref(null);
	const activeGdpr = ref(false);

	function onFormLoad({fields}) {
		fields.forEach(field => {
			if(field.name.startsWith('gdpr_template_api')) {
				if(field.name == 'gdpr_template_api_all') {
					gdprAllField.value = field;
				} else {
					gdprFields.value.push(field);
				}
			} else {
				formFields.value.push(field);
			}
		})
	}

	let allTermsValue = ref(false);

	function onToggleGdpr(name) {
		const objectToUpdate = gdprFields?.value?.find(obj => obj?.name === name);
		objectToUpdate.value = !objectToUpdate.value;

		allTermsValue.value = gdprFields.value.every(obj => obj && obj.value === true);
		gdprAllField.value.value = allTermsValue.value;
	}

	function onToggleAllGdpr(name) {
		allTermsValue.value = gdprFields.value.every(obj => obj && obj.value === true);
		gdprAllField.value.value = allTermsValue.value;

		gdprFields?.value?.forEach(field => {
			field.value = !allTermsValue.value;
		});
	}

	onMounted(() => {
		setTimeout(() => {
			if (gdprFields && gdprFields.value) {
				allTermsValue.value = gdprFields.value.every(obj => obj && obj.value === true);
				if (gdprAllField && gdprAllField.value) {
					gdprAllField.value.value = allTermsValue.value;
				}
			}
		},2000);
	})

	//gtm
	const gtm = useGtmBB();

	function onPageLoad(data) {
		if(!data) return;
		gtm.pageView({
			url: data?.url || '',
			title: data?.title || '',
		});
	}
</script>

<style lang="less" scoped>
	.auth-edit-profile-form{
		max-width: 600px; position: relative;
		&.loading:before{.pseudo(auto, auto); display: flex; align-items: center; justify-content: center; background: rgba(255, 255, 255, 0.3); position: absolute; top: 0; left: 0; right: 0; bottom: 55px; z-index: 11;}
		p.field-address, p.field-house_number{display: inline-block;}
		p.field-address{width: calc(~"70% - 10px"); margin-right: 10px; float: left;}
		p.field-house_number{width: 30%; float: right;}
		p.field-location{clear: both;}
		.field-gender{
			.label-gender{display: block; position: static; font-size: 16px!important; color: @textColor!important; padding: 8px 0 12px;}
			:deep(.field-gender-f){display: inline-block; margin-right: 30px;}
			:deep(.field-gender-m){display: inline-block;}
			:deep(.error){padding-left: 0;}
		}
		.field-birth_day, .field-birth_month{display: inline-block; width: 28%; margin-right: 12px; float: left;}
		.field-birth_year{display: inline-block; width: 40%; float: right;}
		.field-address, .field-country{clear: both;}

		p.field-zipcode, p.field-city, p.field-b_zipcode, p.field-b_city{display: none!important;}
		:deep(p.field-phone input){cursor: not-allowed; color: #ccc;}

		:deep(.autocomplete-container){
			max-height: unset; line-height: 1.4; position: absolute; top: 54px; right: 1px; left: 0; z-index: 111; border: none; overflow: initial; box-shadow: 0 5px 20px 0 rgba(0,0,0,0.15);
			::-webkit-scrollbar {-webkit-appearance: none;width: 4px; background: #E0E8EE;}
			::-webkit-scrollbar-thumb {border-radius: 0;background-color: #99AAB8;-webkit-box-shadow: 0 0 1px rgba(255,255,255,.5);}
			::-webkit-scrollbar-track {border: 1px solid #E0E8EE;}
		}
		:deep(.ui-autocomplete){
			overflow: auto; height: auto; max-height: 300px; background: @white; position: relative; font-size: 15px; margin: 0; z-index: 111;
			&>li{
				display: block; padding: 6px 20px!important; border-bottom: 1px solid @borderColor; color: @textColor; text-decoration: none; cursor: pointer; .transition(all);
				@media (min-width: @t){
					&:hover{background: @blue; color: @white;}
				}
				&.active{background: @blue; color: @white;}
			}
			&>li:last-child{border-bottom: none;}
		}

		@media (max-width: @t){
			max-width: unset;

			:deep(.autocomplete-container){top: 50px;}
		}
		@media (max-width: @ms){
			:deep(.autocomplete-container){top: 44px;}
			:deep(.ui-autocomplete){
				max-height: 200px; font-size: 13px;
				&>li{padding: 5px 15px!important;}
			}
			p.field-address, p.field-house_number{display: block;}
			p.field-address{width: 100%; margin-right: 0; float: unset;}
			p.field-house_number{width: 100%; float: unset;}
			p.field-birth_day, p.field-birth_month, p.field-birth_year{display: block; width: 100%; margin-right: 0; float: unset;}
		}
	}
</style>
